/* eslint-disable max-lines */
export enum StepType {
    Identification = 'Identification',
    LLM = 'LLM',
    NonLLM = 'Non-LLM',
    API = 'API',
    Condition = 'Condition',
    Loop = 'Loop',
    Memory = 'Memory',
}

export type ContextType = 'string' | 'number' | 'dict' | 'list' | 'boolean' | 'any';

export interface ContextVar {
    name: string
    type: ContextType
    preFilled?: boolean
    stepType?: StepType
    stepIndex?: number
}

export interface RoutingStep {
    next: string[];
    show_output: boolean;
}

interface IdentificationStep {
    step_id: string;
    step_type: StepType.Identification;
    input_params: {
        unique_name: string;
        org_id: string;
        sub_org_id: string;
        model_type: string;
        query: string;
    };
}

// ---------- LLM ----------
interface LLMInputParams {
    unique_name: string;
    org_id: string;
    sub_org_id: string;
    model_type: string;
    query: string;
    image_urls: string[];
    image_urls_type: 'array';
    stream: boolean;
}

interface LLMOutputParams {
    output_name: string;
    output_type: 'string' | 'dict' | 'list' | 'number';
    output_schema?: object;
}

export interface LLMStep {
    step_id: string;
    step_type: StepType.LLM | StepType.Identification;
    input_params: LLMInputParams;
    output_params: LLMOutputParams;
    routing: RoutingStep;
}

// ---------- Non-LLM ----------

interface FunctionParam {
    org_id: string;
    sub_org_id: string;
    collection_id: string;
    collection_id_type: 'string';
    query_text: string;
    query_text_type: 'string';
    top_k: string | number;
    top_n: string | number;
    use_rerank: string | boolean;
    filters: Record<string, any>;
    file_keys_filter: string[] | string;
    file_keys_filter_type: 'array';
    search_type: 'vector' | 'file' | 'hybrid' | 'keyword';
    offset: number | string;
    offset_type: 'number';
}

export interface FunctionCall {
    function_name: string;
    function_params: FunctionParam;
}

export interface NonLLMInputParams {
    function_call: FunctionCall;
}

interface NonLLMOutputParams {
    _compiled_text: string;
    _all_fields: string;
    _presigned_urls: string;
    _total_results: string;
    output_names: Record<string, string>;
}

export interface NonLLMStep {
    step_id: string;
    step_type: StepType.NonLLM;
    input_params: NonLLMInputParams;
    output_params: NonLLMOutputParams;
    routing: RoutingStep;
}

// ---------- Condition ----------
interface ConditionInputParams {
    expression: string;
}

type ConditionNextStep = {
    true_branch: string[];
    false_branch: string[];
    show_output: boolean;
}

export interface ConditionStep {
    step_id: string;
    step_type: StepType.Condition;
    routing: ConditionNextStep;
    input_params: ConditionInputParams;
}

// ---------- API ----------

enum MethodEnum {
    POST = 'POST',
    GET = 'GET',
    PUT = 'PUT',
    DELETE = 'DELETE',
}

export type ParamsDef = { key: string, value: string };

export interface APIInputParams {
    _id: string;
    name: string;
    organizationId: string;
    subOrganizationId: string;
    query_params: Record<string, any>;
    input_schema: object;
    output_schema: object;
    method: MethodEnum;
    body: Record<string, any>;
    headers: Record<string, any>;
    auth: Record<string, any>;
    paramsDef: ParamsDef[];
    array_of_dicts_mode: boolean;
}

interface APIOutputParams {
    _output: string;
    _compiled_output: boolean;
    output_names: Record<string, string>;
}

export interface APIStep {
    step_id: string;
    step_type: StepType.API;
    input_params: APIInputParams;
    output_params: APIOutputParams;
    routing: RoutingStep;
}

export interface MemoryStep {
    step_id: string;
    step_type: StepType.Memory;
    input_params: {
        org_id: string;
        sub_org_id: string;
        n: number;
        limit_size: number;
    };
    output_params: {
        output_name: string;
    };
    routing: RoutingStep;
}

export type OpcodeStepInLoop = LLMStep | NonLLMStep | ConditionStep | APIStep | MemoryStep;

export enum ModeEnum {
    range = 'range',
    collection = 'collection'
}

export interface LoopStepCollectionMode {
    mode: 'collection',
    collection: string;
    collection_type: string;
    iterator: string;
    index: string;
    break_condition: string,
    continue_condition: string,
    max_iterations: number
    parallel: boolean,
}

export interface LoopStepRangeMode {
    mode: 'range',
    range_start: number | string;
    range_end: number | string;
    range_step: number | string;
    iterator: string;
    index: string;
    break_condition: string,
    continue_condition: string,
    max_iterations: number
    parallel: boolean,
}

export type LoopInputParams = LoopStepCollectionMode | LoopStepRangeMode;

interface LoopOutputParams {
    output_name: string;
    step_output_keys: Array<any>
}

export interface StepLoop {
    step_id: string;
    step_type: StepType.Loop;
    input_params: LoopInputParams;
    output_params: LoopOutputParams;
    steps: Array<OpcodeStepInLoop>
    routing: RoutingStep;
}

export type OpcodeStep = LLMStep | NonLLMStep | ConditionStep | APIStep | StepLoop | MemoryStep;
// export type OpcodeStepWithIdentification = IdentificationStep | LLMStep | NonLLMStep | ConditionStep | APIStep | StepLoop | MemoryStep;

export type OpcodeStepWithIdentification = [
    IdentificationStep,
    ...OpcodeStep[]
];

export interface Opcode {
    org_id: string
    sub_org_id: string
    identification_step: IdentificationStep
    opcode_id?: string
    name: string
    description: string
    steps: OpcodeStep[]
    createdBy?: string
    created?: string | Date
    updatedBy?: string
    updated?: string | Date
}

export interface OpcodeCreateJson extends Opcode {
    _context_vars: ContextVar[]
}

export interface TemplateOpcode extends Opcode {
    blueprintId: string
}

export interface GeneratorInfo {
    organizationId: string
    subOrganizationId: string
    opcodeId: string
}

export interface Generators {
    questions: GeneratorInfo
    opcode: GeneratorInfo
}

export interface Blueprint {
    entities: object
    opcode: Opcode,
    generators: Generators
}

export interface Question {
    id: string
    question: string
    hint: string
    responseType: string
    options?: string[]
    answer?: string
}

export interface Message {
    id: string
    type: 'bot' | 'user'
    content: string
    hint?: string
    responseType?: string
    timestamp: Date
    questionIndex?: number
    hasInput?: boolean
    options?: Array<unknown>
    answer?: string
}

export enum OpcodeCurrentView {
    blueprintSelection = 'blueprint-selection',
    questionnaire = 'questionnaire',
}

export type CPIEResponse = {
    request_id: string;
    opcode: {
        id: string;
        type: 'AutoOp' | 'ChatOp';
        version: string;
    };
    status: 'ok' | 'running' | 'error';
    timing: {
        started_at: string;
        duration_ms: number;
    };
    outputs: Array<{
        step_id: string;
        step_type: string;
        label: string;
        data: any;
        truncated: boolean;
    }>;
    delta_outputs: Array<any>;
    warnings: string[];
    errors: Array<{
        code: string;
        type: string;
        message: string;
        retryable: boolean;
        step?: {
            id: string;
            type: string;
            index: number;
        };
        time: string;
        details?: any;
    }>;
    debug?: {
        run: any;
        steps: Array<any>;
    };
    event?: {
        type: 'token';
        sequence: number;
    };
    step?: {
        id: string;
        type: string;
        index: number;
    };
    token?: any;
};

export type LoopList = {
    key: string;
    type: string;
    value: Array<any>
    list: Array<any>
};
